<template>
  <section class="report-card">
    <div class="report-card__head">
      <h2 class="report-card__title">备用金</h2>
    </div>

    <div class="reserve-body">
      <div class="ledger-row">
        <span class="ledger-label">期初备用金</span>
        <div class="ledger-value">
          <v-input-money
            v-if="!readonly"
            v-model="report.initial_cash"
            class="ledger-input"
            :is-show-suffix="true"
          />
          <span v-else class="ledger-amount">{{ report.initial_cash | formatYuan }}</span>
        </div>
        <span class="ledger-note">来自上一日期末，可修正</span>
      </div>

      <div class="ledger-row">
        <span class="ledger-label">店长转入</span>
        <div class="ledger-value">
          <v-input-money
            v-if="!readonly"
            v-model="incomeRow.amount"
            class="ledger-input"
            placeholder="0.00"
            :max="999999.99"
            :is-show-suffix="true"
          />
          <span v-else class="ledger-amount" :class="{ 'is-zero': !Number(incomeRow.amount) }">{{ incomeRow.amount | formatYuan }}</span>
        </div>
      </div>

      <div class="ledger-row ledger-row--group">
        <span class="ledger-label">备用金支出</span>
        <div class="ledger-value">
          <span class="ledger-sign">−</span>
          <span class="ledger-amount" :class="{ 'is-zero': !payTotal }">{{ payTotal | formatYuan }}</span>
        </div>
      </div>
      <div
        v-for="(item, index) in report.reserve_pays"
        :key="`reserve-pay-${index}`"
        class="ledger-item"
      >
        <span class="ledger-item-index">{{ index + 1 }}</span>
        <div class="ledger-item-fields">
          <v-input-money
            v-if="!readonly"
            v-model="item.amount"
            class="ledger-item-money"
            placeholder="0.00"
            :max="999999.99"
            :is-show-suffix="true"
          />
          <span v-else class="ledger-item-amount" :class="{ 'is-zero': !Number(item.amount) }">{{ item.amount | formatYuan }}</span>
          <el-select
            v-if="!readonly"
            v-model="item.remark"
            class="ledger-item-remark"
            filterable
            allow-create
            default-first-option
            placeholder="选择用途，或直接输入"
          >
            <el-option
              v-for="option in remarkChoices"
              :key="option"
              :label="option"
              :value="option"
            />
          </el-select>
          <span v-else class="ledger-item-text">{{ item.remark }}</span>
        </div>
        <el-button
          v-if="!readonly"
          type="text"
          class="ledger-item-del"
          @click="removeRow('reserve_pays', index)"
        >删除</el-button>
      </div>

      <div v-if="!readonly" class="ledger-add">
        <el-button type="text" icon="el-icon-plus" @click="addRow('reserve_pays')">新增一项支出</el-button>
      </div>

      <div class="ledger-row ledger-row--total">
        <span class="ledger-label">期末备用金</span>
        <div class="ledger-value">
          <span class="ledger-amount ledger-amount--strong" :class="{ 'is-zero': !balance }">{{ balance | formatYuan }}</span>
        </div>
        <span class="ledger-note">结转到次日</span>
      </div>

      <div class="ledger-row ledger-row--foot">
        <span class="ledger-label">当日现金收入</span>
        <div class="ledger-value">
          <span class="ledger-amount" :class="{ 'is-zero': !report.cash_income }">{{ report.cash_income | formatYuan }}</span>
        </div>
        <span class="ledger-note">计入收入，不并入备用金</span>
      </div>
    </div>
  </section>
</template>

<script>
import { plus, minus } from '@/filters';

export default {
  name: 'DailyReportReserve',
  props: {
    report: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
  },
  data: () => ({
    // 备用金支出的常见用途（按历史数据出现频率取的前几项），
    // 仍然是「文字」，下拉只是录入辅助，也可以直接输入新词
    remarkOptions: ['快递费', '杂物', '物业费', '食品', '手机充值', '修玻璃', '广告', '提成'],
  }),
  computed: {
    // 新增备用金只有「店长转入」一种，固定一行
    incomeRow() {
      return this.report.reserve_incomes[0] || { amount: '', remark: '' };
    },
    // 选项 = 预设常用词 + 本次已填过的词，保证自定义/历史值也能正常显示
    remarkChoices() {
      const used = (this.report.reserve_pays || [])
        .map(item => (item.remark || '').trim())
        .filter(Boolean);
      return Array.from(new Set([...used, ...this.remarkOptions]));
    },
    addTotal() {
      return this.report.reserve_incomes.reduce(
        (total, item) => plus(total, item.amount || 0),
        0,
      );
    },
    payTotal() {
      return this.report.reserve_pays.reduce(
        (total, item) => plus(total, item.amount || 0),
        0,
      );
    },
    // 期初 + 新增 - 支出 = 期末（期末即次日期的期初）
    balance() {
      return minus(plus(this.report.initial_cash || 0, this.addTotal), this.payTotal);
    },
  },
  watch: {
    addTotal: 'emitChange',
    payTotal: 'emitChange',
    'report.initial_cash': 'emitChange',
  },
  methods: {
    addRow(key) {
      this.report[key].push({ amount: '', remark: '' });
    },
    removeRow(key, index) {
      this.report[key].splice(index, 1);
    },
    emitChange() {
      if (this.readonly) return;
      this.$emit('change', {
        reserve_income: this.addTotal,
        expenditure: this.payTotal,
        reserve_balance: this.balance,
      });
    },
  },
};
</script>

<style src="../report-card.css"></style>

<style scoped>
.reserve-body {
  box-sizing: border-box;
  max-width: 760px;
  padding: 8px 24px 16px;
}

.ledger-row {
  display: flex;
  align-items: center;
  min-height: 52px;
}

.ledger-label {
  flex: 0 0 200px;
  color: #606266;
  font-size: 13px;
}

.ledger-value {
  display: flex;
  flex: 0 0 200px;
  align-items: center;
  justify-content: flex-end;
}

.ledger-input {
  width: 200px;
}

.ledger-amount {
  color: #1f2d3d;
  font-size: 15px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}

.ledger-amount.is-zero,
.ledger-item-amount.is-zero {
  color: #c0c4cc;
  font-weight: 400;
}

.ledger-amount--strong {
  color: #1f2d3d;
  font-size: 18px;
}

.ledger-sign {
  margin-right: 6px;
  color: #909399;
  font-size: 14px;
}

/* 支出小计是「这一组的总数」，比单值行更重一档，避免看起来比明细还轻 */
.ledger-row--group .ledger-amount {
  font-weight: 600;
}

.ledger-note {
  margin-left: 16px;
  color: #909399;
  font-size: 12px;
}

.ledger-add {
  padding-left: 200px;
  line-height: 1;
}

.ledger-add /deep/ .el-button {
  padding: 4px 0;
  font-size: 13px;
}

.ledger-item {
  display: flex;
  align-items: center;
  padding: 4px 0;
}

/* 序号占标签列，金额与标签保持同一条竖线 */
.ledger-item-index {
  flex: 0 0 200px;
  color: #c0c4cc;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.ledger-item-readonly-index {
  flex: 0 0 200px;
}

.ledger-item-fields {
  display: flex;
  flex: 1;
  align-items: center;
}

.ledger-item-money {
  width: 200px;
}

/* 明细金额：比小计低一档（14/400/次要色），不能和汇总数字一样重 */
.ledger-item-amount {
  width: 200px;
  color: #606266;
  font-size: 14px;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  text-align: right;
}

.ledger-item-remark {
  flex: 1;
  max-width: 420px;
  margin-left: 12px;
}

.ledger-item-text {
  flex: 1;
  margin-left: 12px;
  color: #909399;
  font-size: 13px;
}

.ledger-item-del {
  margin-left: 8px;
  color: #f56c6c;
}

.ledger-row--group {
  padding-top: 6px;
  border-top: 1px solid #f5f7fa;
}

.ledger-row--total {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #ebeef5;
}

.ledger-row--foot .ledger-label,
.ledger-row--foot .ledger-amount {
  color: #909399;
  font-weight: 400;
}

@media (max-width: 1199px) {
  .reserve-body {
    padding: 8px 16px 14px;
  }

  .ledger-label {
    flex-basis: 160px;
  }

  .ledger-value {
    flex-basis: 160px;
  }

  .ledger-item-index {
    flex-basis: 160px;
  }

  .ledger-item-money,
  .ledger-item-amount {
    width: 160px;
  }
}

@media (max-width: 768px) {
  .reserve-body {
    padding: 6px 14px 12px;
  }

  .ledger-row {
    flex-wrap: wrap;
    align-items: flex-start;
    padding: 8px 0;
  }

  .ledger-label {
    flex: 0 0 100%;
    margin-bottom: 6px;
  }

  .ledger-value {
    flex: 0 0 auto;
    justify-content: flex-start;
  }

  .ledger-item-index {
    flex: 0 0 24px;
  }

  .ledger-item-fields {
    flex-wrap: wrap;
  }

  .ledger-item-remark,
  .ledger-item-text {
    margin-top: 6px;
    margin-left: 0;
  }
}
</style>
